import React from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Box, Button, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FoodsIcon } from "@foodpilot/foods";

import { URLHandler } from "src/services/URLHandler";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";

import { IngredientReferenceListContextProvider } from "./IngredientReferenceListContext";
import { useIngredientReferenceListContext } from "./useIngredientReferenceListContext";
import { IngredientReferenceGrid } from "./IngredientReferenceGrid";
import { IngredientReferenceModal } from "./IngredientReferenceModal";

const IngredientReferenceListContent: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFeatureActivated } = useApplicationParameters();

    const { search, setSearch, showCreateModal, setShowCreateModal } = useIngredientReferenceListContext();

    if (!isFeatureActivated("FS-platform-ingredient-references")) {
        return <div>{t("common.accessDenied", "Access denied")}</div>;
    }

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={() => navigate(URLHandler.settings.configuration())}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                        {t("ingredientReferences.title", "Référentiel ingrédients")}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        padding: "24px 32px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "24px",
                    }}
                >
                    <TextField
                        placeholder={t("ingredientReferences.search", "Search references...")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#A3A2A0", fontSize: "1.25rem" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            maxWidth: "323px",
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "24px",
                                minHeight: "40px",
                                backgroundColor: "white",
                                "& fieldset": { borderColor: "#E7E6E4" },
                                "&:hover fieldset": { borderColor: "#D0D0D0" },
                                "&.Mui-focused fieldset": { borderColor: "primary.main" },
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "0.875rem",
                                padding: "0px 16px !important",
                                color: "#393A36",
                            },
                        }}
                    />

                    <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        <Button
                            variant="contained"
                            startIcon={<FoodsIcon icon="plus" size={1} />}
                            onClick={() => setShowCreateModal(true)}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    color: "white !important",
                                },
                            })}
                        >
                            {t("ingredientReferences.add", "Add reference")}
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ padding: "0px 32px 32px" }}>
                    <IngredientReferenceGrid />
                </Box>
            </Paper>

            <IngredientReferenceModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={() => setShowCreateModal(false)}
                editing={undefined}
            />
        </Box>
    );
};

export const IngredientReferenceList: React.FC = () => {
    return (
        <IngredientReferenceListContextProvider>
            <IngredientReferenceListContent />
        </IngredientReferenceListContextProvider>
    );
};
